import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Flag, Plus, Trash2, ShieldAlert } from "lucide-react";

export default function ReportsTab() {
  const { lang } = useI18n();
  const [reports, setReports] = useState([]);
  const [words, setWords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [newWord, setNewWord] = useState("");

  const loadReports = useCallback(() => {
    apiClient.get("/admin/reports", { params: { status: statusFilter || undefined } })
      .then(({ data }) => setReports(data.items || []));
  }, [statusFilter]);

  const loadWords = useCallback(() => {
    apiClient.get("/admin/banned-words").then(({ data }) => setWords(data.items || []));
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { loadWords(); }, [loadWords]);

  const setStatus = async (id, status) => {
    await apiClient.patch(`/admin/reports/${id}`, { status });
    loadReports();
  };
  const remove = async (id) => {
    if (!window.confirm(lang === "ar" ? "حذف البلاغ؟" : "Delete report?")) return;
    await apiClient.delete(`/admin/reports/${id}`);
    loadReports();
  };

  const addWord = async (e) => {
    e.preventDefault();
    if (!newWord.trim()) return;
    try {
      await apiClient.post("/admin/banned-words", { word: newWord.trim() });
      setNewWord("");
      loadWords();
    } catch (err) {
      alert(err.response?.data?.detail || "Error");
    }
  };
  const delWord = async (id) => {
    await apiClient.delete(`/admin/banned-words/${id}`);
    loadWords();
  };

  return (
    <div data-testid="admin-reports" className="space-y-8">
      <div>
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <h2 className="font-display text-2xl flex items-center gap-2"><Flag className="w-5 h-5 text-[#8a6f2e]" /> {lang === "ar" ? "البلاغات" : "Reports"} ({reports.length})</h2>
          <select className="input-dark max-w-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="reports-status-filter">
            <option value="">{lang === "ar" ? "كل الحالات" : "All statuses"}</option>
            <option value="open">open</option>
            <option value="reviewed">reviewed</option>
            <option value="resolved">resolved</option>
            <option value="rejected">rejected</option>
          </select>
        </div>
        {reports.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center text-[#6b6b6b]">{lang === "ar" ? "لا توجد بلاغات." : "No reports."}</div>
        ) : (
          <div className="space-y-3">
            {reports.map(r => (
              <div key={r.id} className="surface rounded-xl p-5" data-testid={`report-${r.id}`}>
                <div className="flex justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display text-base">{r.reason}</div>
                    <div className="text-xs text-[#6b6b6b] mt-1">{r.target_type} · {r.target_id?.slice(0, 8)} · {r.reporter_email} · {new Date(r.created_at).toLocaleString()}</div>
                    {r.description && <p className="text-sm text-[#3a3a3a] mt-2">{r.description}</p>}
                  </div>
                  <span className="pill pill-accent self-start">{r.status}</span>
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  {["open", "reviewed", "resolved", "rejected"].map(s => (
                    <button key={s} onClick={() => setStatus(r.id, s)} className={`btn-ghost text-xs ${r.status === s ? "border-[#c9a55c]" : ""}`} data-testid={`report-${r.id}-set-${s}`}>{s}</button>
                  ))}
                  <button onClick={() => remove(r.id)} className="btn-ghost text-xs text-red-700 border-red-300"><Trash2 className="w-3 h-3" /> {lang === "ar" ? "حذف" : "Delete"}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display text-xl mb-4 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "الكلمات المحظورة" : "Banned words"} ({words.length})</h3>
        <form onSubmit={addWord} className="flex gap-2 mb-4">
          <input className="input-dark flex-1 max-w-xs" placeholder={lang === "ar" ? "اضف كلمة..." : "Add word..."} value={newWord} onChange={(e) => setNewWord(e.target.value)} data-testid="banned-word-input" />
          <button type="submit" className="btn-primary text-xs" data-testid="banned-word-add"><Plus className="w-3 h-3" /> {lang === "ar" ? "إضافة" : "Add"}</button>
        </form>
        <div className="flex gap-2 flex-wrap">
          {words.length === 0 ? <div className="text-sm text-[#6b6b6b]">{lang === "ar" ? "لا شيء بعد" : "Nothing yet"}</div> : words.map(w => (
            <span key={w.id} className="pill" data-testid={`banned-word-${w.id}`}>
              {w.word}
              <button onClick={() => delWord(w.id)} className="ms-2 text-red-700 hover:text-red-900"><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
